/**
 * The read side of a person: who they are, and when they are reachable.
 *
 * Two endpoints, both already live and both authenticated as the VIEWER, so
 * nothing here can show more than the signed-in user is entitled to see:
 *
 *   GET  /v1/users/:id          the full `User` — job title, email, phone,
 *                               department, office, timezone. `/v1/users` (the
 *                               directory) returns `UserRef`, which is name +
 *                               avatar and nothing else, so the card has to ask
 *                               for the person by id rather than filter a list.
 *   POST /v1/calendar/free-busy busy blocks and the configured working hours.
 *                               `workingHours` is `null` when that person never
 *                               set any — the card then omits the line instead
 *                               of inventing 9-to-5.
 */
import type { FreeBusyResponse } from '@cyphex/types/calendar';
import type { UserId } from '@cyphex/types/common';
import type { User } from '@cyphex/types/identity';
import { apiGet, apiPost } from '@/lib/api/client';

/** One person's slice of a free/busy answer. */
export type PersonAvailability = FreeBusyResponse['users'][number];

/** How far ahead the card looks for the next commitment. */
const AVAILABILITY_HORIZON_HOURS = 24;

export const peopleApi = {
  getUser: (userId: UserId, signal?: AbortSignal): Promise<User> =>
    apiGet<User>(`/users/${userId}`, signal),

  /**
   * Availability for ONE person. The endpoint is a batch (up to 100 ids), but a
   * profile card is opened for one person at a time and each card caches its
   * own answer, so batching here would only add a coordinator for no gain.
   */
  getAvailability: async (userId: UserId, signal?: AbortSignal): Promise<PersonAvailability | null> => {
    const from = new Date();
    const to = new Date(from.getTime() + AVAILABILITY_HORIZON_HOURS * 60 * 60 * 1000);
    const response = await apiPost<FreeBusyResponse>(
      '/calendar/free-busy',
      { userIds: [userId], from: from.toISOString(), to: to.toISOString() },
      signal === undefined ? {} : { signal },
    );
    return response.users[0] ?? null;
  },
} as const;
